import { useState } from "react";
import { AlertTriangle, Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const money = (value) => (value === null || value === undefined || value === "" ? "—" : `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const PROVIDER_SHARE = 0.7;
const LOW_MARGIN = 0.3;

export function marginOf(clientFee, providerFee) {
  const client = Number(clientFee);
  const provider = Number(providerFee);
  if (clientFee === "" || providerFee === "" || clientFee === null || providerFee === null || clientFee === undefined || providerFee === undefined || Number.isNaN(client) || Number.isNaN(provider) || client <= 0) return null;
  const margin = client - provider;
  const pct = Math.round((margin / client) * 1000) / 10;
  return { margin, pct, low: pct < LOW_MARGIN * 100 };
}

export const MarginCell = ({ clientFee, providerFee, testId }) => {
  const m = marginOf(clientFee, providerFee);
  if (!m) return <span className="rate-margin" data-testid={testId}>—</span>;
  return <span className={`rate-margin ${m.low ? "margin-low" : ""}`} data-testid={testId}>{money(m.margin)} <small>({m.pct.toFixed(1)}%)</small></span>;
};

const prefill = (clientFee) => (clientFee === "" ? "" : String(Math.round(Number(clientFee) * PROVIDER_SHARE * 100) / 100));

function RateRow({ item, types, clientId, onChanged, onError, onCancel }) {
  const isNew = !item.id;
  const [draft, setDraft] = useState({ name: item.name || "", evaluation_types: item.evaluation_types || [], client_fee: item.client_fee ?? "", provider_fee: item.provider_fee ?? "", is_default: !!item.is_default });
  const [busy, setBusy] = useState(false);
  const margin = marginOf(draft.client_fee, draft.provider_fee);
  const dirty = isNew || item.needs_repair || draft.name !== item.name || JSON.stringify(draft.evaluation_types) !== JSON.stringify(item.evaluation_types) || String(draft.client_fee) !== String(item.client_fee) || String(draft.provider_fee) !== String(item.provider_fee) || draft.is_default !== !!item.is_default;
  const valid = draft.name.trim() && draft.evaluation_types.length && draft.client_fee !== "" && draft.provider_fee !== "";
  const key = item.id || "new";

  const toggleType = (type) => setDraft({ ...draft, evaluation_types: draft.evaluation_types.includes(type) ? draft.evaluation_types.filter((t) => t !== type) : [...draft.evaluation_types, type] });

  async function save() {
    setBusy(true);
    try {
      const payload = { name: draft.name.trim(), evaluation_types: draft.evaluation_types, client_fee: Number(draft.client_fee), provider_fee: Number(draft.provider_fee), is_default: draft.is_default };
      const response = isNew ? await api.post("/evaluation-billing/rates", { ...payload, client_id: clientId }) : await api.put(`/evaluation-billing/rates/${item.id}`, payload);
      const applied = response.data.applied_to ? ` Applied to ${response.data.applied_to} completed evaluation(s) that had no rate.` : "";
      onChanged(`${response.data.client_name} · ${response.data.name} saved.${applied}`);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the “${item.name}” rate for this client? Completed evaluations keep the fee already recorded.`)) return;
    setBusy(true);
    try {
      await api.delete(`/evaluation-billing/rates/${item.id}`);
      onChanged(`${item.name} removed.`);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className={`${margin?.low ? "is-low-margin" : ""} ${item.needs_repair ? "is-needs-repair" : ""}`} data-testid={`rate-row-${key}`}>
      <td>
        <input className="rate-name-input" placeholder="e.g. OT eval / Speech eval" value={draft.name} data-testid={`rate-name-${key}`} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        {item.needs_repair && <em className="compliance-result compliance-result-possible rate-repair-badge" title={`Missing: ${(item.missing || []).join(", ")}`} data-testid={`rate-needs-repair-${key}`}><AlertTriangle size={12} /> Needs repair · missing {(item.missing || []).join(", ")}</em>}
      </td>
      <td>
        <div className="rate-type-chips" data-testid={`rate-types-${key}`}>
          {types.map((type) => <button type="button" key={type} className={draft.evaluation_types.includes(type) ? "is-on" : ""} data-testid={`rate-type-${key}-${type}`} onClick={() => toggleType(type)}>{type}</button>)}
        </div>
      </td>
      <td><input type="number" min="0" step="0.01" placeholder="Client fee" value={draft.client_fee} data-testid={`rate-client-fee-${key}`} onChange={(e) => setDraft({ ...draft, client_fee: e.target.value, provider_fee: prefill(e.target.value) })} /></td>
      <td><input type="number" min="0" step="0.01" placeholder="Provider fee" value={draft.provider_fee} data-testid={`rate-provider-fee-${key}`} onChange={(e) => setDraft({ ...draft, provider_fee: e.target.value })} /></td>
      <td><MarginCell clientFee={draft.client_fee} providerFee={draft.provider_fee} testId={`rate-margin-${key}`} /></td>
      <td><label className="rate-default-toggle"><input type="checkbox" checked={draft.is_default} data-testid={`rate-default-${key}`} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} /> Default</label></td>
      <td><small>{item.updated_by_name ? `${item.updated_by_name} · ${new Date(item.updated_at).toLocaleDateString()}` : "New"}</small></td>
      <td className="credential-actions rate-actions">
        <button type="button" className="button button-secondary" disabled={busy || !dirty || !valid} data-testid={`rate-save-${key}`} onClick={save}><Save size={13} /> Save</button>
        {isNew ? <button type="button" className="button button-ghost" data-testid="rate-cancel-new" onClick={onCancel}>Cancel</button> : <button type="button" className="projection-icon-button" aria-label={`Remove ${item.name}`} disabled={busy} data-testid={`rate-delete-${key}`} onClick={remove}><Trash2 size={14} /></button>}
      </td>
    </tr>
  );
}

export const RateTable = ({ data, onChanged, onError }) => {
  const [clientId, setClientId] = useState(data.clients[0]?.id || "");
  const [adding, setAdding] = useState(false);
  const client = data.clients.find((c) => c.id === clientId);
  const items = data.items.filter((item) => item.client_id === clientId);
  const lowCount = items.filter((item) => item.low_margin).length;
  const orphans = data.items.filter((item) => item.needs_repair && !item.client_id);
  const repairCount = data.items.filter((item) => item.needs_repair).length;
  const changed = (message) => {
    setAdding(false);
    onChanged(message);
  };
  async function removeOrphan(item) {
    if (!window.confirm(`Remove the “${item.name || "unnamed"}” rate? It has no client, so it cannot be repaired.`)) return;
    try {
      await api.delete(`/evaluation-billing/rates/${item.id}`);
      onChanged("Rate removed.");
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }
  return (
    <section className="compliance-card" data-testid="evaluation-rate-table">
      {repairCount > 0 && (
        <p className="form-message warning-message billing-unpriced-banner" data-testid="rate-repair-banner">
          <AlertTriangle size={15} /> {repairCount} rate row(s) were saved without every field and are skipped when pricing evaluations. They are marked “Needs repair” below — fill in the missing fields and Save.
          {orphans.map((item) => <span key={item.id} className="rate-orphan" data-testid={`rate-orphan-${item.id}`}> · “{item.name || "unnamed"}” has no client <button type="button" className="link-button" data-testid={`rate-orphan-remove-${item.id}`} onClick={() => removeOrphan(item)}>Remove</button></span>)}
        </p>
      )}
      <div className="section-heading">
        <div>
          <h3>Rate table</h3>
          <p>Each client has its own list of billable evaluation types. Enter the client fee and the provider fee pre-fills at 70% (a 30% margin) — both stay editable. Rows under 30% margin are highlighted in amber. A provider's own rate card overrides the provider fee here.</p>
        </div>
        <div className="rate-table-controls">
          <select value={clientId} data-testid="rate-client-select" onChange={(e) => { setClientId(e.target.value); setAdding(false); }}>
            {data.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button type="button" className="button button-primary" disabled={!client || adding} data-testid="rate-add-button" onClick={() => setAdding(true)}><Plus size={14} /> Add evaluation type</button>
        </div>
      </div>
      {client ? (
        <div className="credential-table-wrap rate-table-wrap">
          <table>
            <thead><tr><th>Evaluation type</th><th>Applies to requests typed</th><th>Client fee</th><th>Provider fee</th><th>Margin</th><th /><th>Last updated</th><th /></tr></thead>
            <tbody>
              {items.map((item) => <RateRow key={`${item.id}-${item.updated_at}`} item={item} types={data.evaluation_types} clientId={clientId} onChanged={changed} onError={onError} />)}
              {adding && <RateRow key="new" item={{}} types={data.evaluation_types} clientId={clientId} onChanged={changed} onError={onError} onCancel={() => setAdding(false)} />}
            </tbody>
          </table>
          {!items.length && !adding && <p className="operations-empty-state" data-testid="rate-table-empty">No rates for {client.name} yet. Add the first evaluation type.</p>}
          {items.length > 0 && <p className="pairing-meta" data-testid="rate-table-summary">{items.length} rate(s){lowCount ? ` · ${lowCount} under 30% margin` : ""}. When several rows apply to the same request type, mark one as Default or choose the rate on each completed evaluation.</p>}
        </div>
      ) : <p className="operations-empty-state">No active clients.</p>}
    </section>
  );
};

export { money };
